/** @jsx jsx */
import { FC, useState, useEffect } from 'react';
import { jsx } from '@emotion/core';
import {
  Box,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Checkbox,
  FormGroup,
  MenuItem,
  Select,
  InputLabel,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import TabSelector from '../Atoms/TabSelector';
import GeneralPrimaryButton from '../Atoms/GeneralPrimaryButton';
import GeneralPrimaryGhostButton from '../Atoms/GeneralPrimaryGhostButton';
import { createBarthelIndexAPIInstance } from '../../api/BarthelIndexAPI';
import { createLifeCheckAPIInstance } from '../../api/LifeCheck';
import { getAppUserData } from '../../api/AppUser';
import { MoffAPIActionType } from '../../constants/MoffAPI';
import { BarthelIndexData } from '../../constants/BarthelIndexAPI';
import { LifecheckData } from '../../constants/LifecheckAPI';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: '50px',
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 1px 2px 1px rgba(0, 0, 0, 0.3)',
  },
  headerTitle: {
    padding: '25px 30px 25px 20px',
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#7c644e',
    borderRadius: '10px 10px 0 0',
  },
  formSection: {
    marginBottom: theme.spacing(4),
  },
  formTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(0.5),
  },
  numberInput: {
    width: '100px',
  },
  selectField: {
    minWidth: '100px',
  },
  tabContent: {
    padding: theme.spacing(3),
  },
  adlValue: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  recordSelector: {
    marginBottom: theme.spacing(2),
  },
  adlItem: {
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  adlTitle: {
    fontWeight: 'bold',
    fontSize: '0.85rem',
    marginRight: theme.spacing(1),
    width: '150px',
    flexShrink: 0,
  },
  adlData: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 1,
  },
  adlDataSource: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '0.85rem',
  },
  noData: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
}));

interface ScientificNursingCarePromotionFormProps {
  userId: string;
  evaluationData?: any;
  isEditMode?: boolean;
  onSave: (formData: any) => void;
  onCancel: () => void;
  onDuplicate?: (formData: any) => void;
  onDelete?: () => void;
}

const ScientificNursingCarePromotionForm: FC<ScientificNursingCarePromotionFormProps> = ({
  userId,
  evaluationData,
  isEditMode = false,
  onSave,
  onCancel,
  onDuplicate,
  onDelete,
}) => {
  const classes = useStyles();

  // タブ関連のステート
  const [selectedTab, setSelectedTab] = useState(0);
  const tabLabels = ['必須項目', '任意項目'];

  // 評価日のステート
  const [evaluate_date_year, setEvaluateDateYear] = useState<string>(evaluationData?.evaluate_date_year || '');
  const [evaluate_date_month, setEvaluateDateMonth] = useState<string>(evaluationData?.evaluate_date_month || '');
  const [evaluate_date_day, setEvaluateDateDay] = useState<string>(evaluationData?.evaluate_date_day || '');

  // 利用者情報のステート
  const [status, setStatus] = useState<string>(evaluationData?.status || 'start');

  // ADLのステート
  const [selectedRecordType, setSelectedRecordType] = useState<string>(
    evaluationData?.adl_relation_type || evaluationData?.selected_record_type || 'lifecheck',
  );
  const [selectedRecordId, setSelectedRecordId] = useState<string>(
    evaluationData?.adl_relation_selected_id || evaluationData?.selected_record_id || '',
  );

  // BarthelとLifeCheckのデータ
  const [barthelDataList, setBarthelDataList] = useState<BarthelIndexData[]>([]);
  const [lifeCheckDataList, setLifeCheckDataList] = useState<LifecheckData[]>([]);
  const [selectedBarthelData, setSelectedBarthelData] = useState<BarthelIndexData | null>(null);
  const [selectedLifeCheckData, setSelectedLifeCheckData] = useState<LifecheckData | null>(null);

  // 口腔・栄養のステート
  const [height, setHeight] = useState<string>(evaluationData?.height?.toString() || '');
  const [weight, setWeight] = useState<string>(evaluationData?.weight?.toString() || '');
  const [denture, setDenture] = useState<string>(evaluationData?.denture || 'false');
  const [choke, setChoke] = useState<string>(evaluationData?.choke || 'false');
  const [stains_on_teeth, setStainsOnTeeth] = useState<string>(evaluationData?.stains_on_teeth || 'false');
  const [condition_of_gums, setConditionOfGums] = useState<string>(evaluationData?.condition_of_gums || 'false');

  // 認知症のステート
  const [diagnosis_of_dementia_01, setDiagnosisOfDementia01] = useState<boolean>(
    evaluationData?.diagnosis_of_dementia_01 || false,
  );
  const [diagnosis_of_dementia_02, setDiagnosisOfDementia02] = useState<boolean>(
    evaluationData?.diagnosis_of_dementia_02 || false,
  );
  const [diagnosis_of_dementia_03, setDiagnosisOfDementia03] = useState<boolean>(
    evaluationData?.diagnosis_of_dementia_03 || false,
  );

  // 任意項目のステート
  // ①-1 身近なものを置いた場所を覚えているか
  const [diagnosis_of_dementia_evaluate_1_1, setDiagnosisOfDementiaEvaluate1_1] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_1_1 || '5',
  );
  // ①-2 日常的な出来事をどのくらい覚えているか
  const [diagnosis_of_dementia_evaluate_1_2, setDiagnosisOfDementiaEvaluate1_2] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_1_2 || '5',
  );
  // ② 現在の日付や場所等の認識
  const [diagnosis_of_dementia_evaluate_2, setDiagnosisOfDementiaEvaluate2] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_2 || '5',
  );
  // ③ 会話でのコミュニケーション
  const [diagnosis_of_dementia_evaluate_3, setDiagnosisOfDementiaEvaluate3] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_3 || '5',
  );
  // ④ 服薬
  const [diagnosis_of_dementia_evaluate_4, setDiagnosisOfDementiaEvaluate4] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_4 || '5',
  );
  // ⑤ 着替え
  const [diagnosis_of_dementia_evaluate_5, setDiagnosisOfDementiaEvaluate5] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_5 || '5',
  );
  // ⑥ 電化製品の操作
  const [diagnosis_of_dementia_evaluate_6, setDiagnosisOfDementiaEvaluate6] = useState<string>(
    evaluationData?.diagnosis_of_dementia_evaluate_6 || '5',
  );

  // Vitality index
  const [vitality_index_01, setVitalityIndex01] = useState<string>(evaluationData?.vitality_index_01 || '2');
  const [vitality_index_02, setVitalityIndex02] = useState<string>(evaluationData?.vitality_index_02 || '2');
  const [vitality_index_03, setVitalityIndex03] = useState<string>(evaluationData?.vitality_index_03 || '2');
  const [vitality_index_04, setVitalityIndex04] = useState<string>(evaluationData?.vitality_index_04 || '2');
  const [vitality_index_05, setVitalityIndex05] = useState<string>(evaluationData?.vitality_index_05 || '2');

  // DBD13
  const [dbd13_01, setDbd13_01] = useState<string>(evaluationData?.dbd13_01 || '0');
  const [dbd13_02, setDbd13_02] = useState<string>(evaluationData?.dbd13_02 || '0');
  const [dbd13_03, setDbd13_03] = useState<string>(evaluationData?.dbd13_03 || '0');
  const [dbd13_04, setDbd13_04] = useState<string>(evaluationData?.dbd13_04 || '0');
  const [dbd13_05, setDbd13_05] = useState<string>(evaluationData?.dbd13_05 || '0');
  const [dbd13_06, setDbd13_06] = useState<string>(evaluationData?.dbd13_06 || '0');
  const [dbd13_07, setDbd13_07] = useState<string>(evaluationData?.dbd13_07 || '0');
  const [dbd13_08, setDbd13_08] = useState<string>(evaluationData?.dbd13_08 || '0');
  const [dbd13_09, setDbd13_09] = useState<string>(evaluationData?.dbd13_09 || '0');
  const [dbd13_10, setDbd13_10] = useState<string>(evaluationData?.dbd13_10 || '0');
  const [dbd13_11, setDbd13_11] = useState<string>(evaluationData?.dbd13_11 || '0');
  const [dbd13_12, setDbd13_12] = useState<string>(evaluationData?.dbd13_12 || '0');
  const [dbd13_13, setDbd13_13] = useState<string>(evaluationData?.dbd13_13 || '0');

  // ICFステージング
  const [basic_operation, setBasicOperation] = useState<string>(evaluationData?.basic_operation || '5');
  const [walking_moving, setWalkingMoving] = useState<string>(evaluationData?.walking_moving || '5');
  const [cognitive_orientation, setCognitiveOrientation] = useState<string>(
    evaluationData?.cognitive_orientation || '5',
  );
  const [cognitive_communication, setCognitiveCommunication] = useState<string>(
    evaluationData?.cognitive_communication || '5',
  );
  const [cognitive_mental_activity, setCognitiveMentalActivity] = useState<string>(
    evaluationData?.cognitive_mental_activity || '5',
  );
  const [swallowing_function, setSwallowingFunction] = useState<string>(evaluationData?.swallowing_function || '5');
  const [eating_assistance, setEatingAssistance] = useState<string>(evaluationData?.eating_assistance || '5');
  const [excretion, setExcretion] = useState<string>(evaluationData?.excretion || '5');
  const [bathing, setBathing] = useState<string>(evaluationData?.bathing || '5');
  const [oral_care, setOralCare] = useState<string>(evaluationData?.oral_care || '5');
  const [dressing, setDressing] = useState<string>(evaluationData?.dressing || '5');
  const [dressing_action, setDressingAction] = useState<string>(evaluationData?.dressing_action || '5');
  const [leisure, setLeisure] = useState<string>(evaluationData?.leisure || '5');
  const [communication, setCommunication] = useState<string>(evaluationData?.communication || '5');

  // 年の選択肢を生成
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years.map((year) => (
      <option key={year} value={year}>
        {year}
      </option>
    ));
  };

  // 月の選択肢を生成
  const generateMonthOptions = () => {
    const months = [];
    for (let i = 1; i <= 12; i++) {
      months.push(i);
    }
    return months.map((month) => (
      <option key={month} value={month}>
        {month}
      </option>
    ));
  };

  // 日の選択肢を生成
  const generateDayOptions = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(i);
    }
    return days.map((day) => (
      <option key={day} value={day}>
        {day}
      </option>
    ));
  };

  // タブ切り替え処理
  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  // BarthelとLifeCheckのデータリストを取得
  useEffect(() => {
    const fetchBarthelAndLifeCheckData = async () => {
      try {
        // AppUserAPIからuniqueIdを取得
        const appUserResponse = await getAppUserData(userId, MoffAPIActionType.LOADING);
        if (!appUserResponse || !appUserResponse.data || !appUserResponse.data.unique_id) {
          console.error('ユーザーのuniqueIdが取得できませんでした');
          return;
        }

        const uniqueId = appUserResponse.data.unique_id;
        console.log('取得したuniqueId:', uniqueId);

        // BarthelIndexAPIのインスタンスを取得
        const [barthelError, barthelAPI] = createBarthelIndexAPIInstance();
        if (!barthelError && barthelAPI) {
          const today = new Date();
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(today.getFullYear() - 1);

          const startDate = oneYearAgo.toISOString().split('T')[0];
          const endDate = today.toISOString().split('T')[0];

          try {
            console.log('Barthel Indexデータを取得中...', uniqueId, startDate, endDate);
            const barthelResponse = await barthelAPI.getBiFunc(uniqueId, startDate, endDate);
            console.log('Barthel Indexデータ取得結果:', barthelResponse);

            if (barthelResponse.data && barthelResponse.data.length > 0) {
              console.log('Barthel Indexデータ件数:', barthelResponse.data.length);
              // データを日付降順でソート
              const sortedBarthelData = [...barthelResponse.data].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
              );
              // ソートしたデータリストを保存
              setBarthelDataList(sortedBarthelData);

              // 評価データに選択されたレコードがある場合はそれを選択
              if (evaluationData?.selected_record_type === 'barthel' && evaluationData?.selected_record_id) {
                const selectedData = sortedBarthelData.find(
                  (item: BarthelIndexData) => item.created_at === evaluationData.selected_record_id,
                );
                console.log('選択されたBarthel Indexデータ:', selectedData);
                console.log('選択されたレコードID:', evaluationData.selected_record_id);
                if (selectedData) {
                  setSelectedBarthelData(selectedData);
                  setSelectedRecordId(evaluationData.selected_record_id);
                }
              } else if (!evaluationData?.selected_record_id) {
                // デフォルトで最新のデータを選択（ソート済みなので配列の先頭が最新）
                const latestBarthelData = sortedBarthelData[0];
                setSelectedBarthelData(latestBarthelData);
                setSelectedRecordId(latestBarthelData.created_at || '');
              }
            } else {
              console.warn('Barthel Indexデータが見つかりませんでした');
            }
          } catch (error) {
            console.error('Barthel Indexデータの取得に失敗しました:', error);
          }
        }

        // LifeCheckAPIのインスタンスを取得
        const [lifeCheckError, lifeCheckAPI] = createLifeCheckAPIInstance();
        if (!lifeCheckError && lifeCheckAPI) {
          try {
            // レコード取得
            console.log('LifeCheckレコード取得中...', uniqueId);
            const lifeCheckResponse = await lifeCheckAPI.getRecordCountAPI(uniqueId);
            console.log('LifeCheckレコード取得結果:', lifeCheckResponse);

            if (lifeCheckResponse.data && Array.isArray(lifeCheckResponse.data) && lifeCheckResponse.data.length > 0) {
              console.log('LifeCheckレコード総数:', lifeCheckResponse.data.length);

              // データをsequenceで降順ソート
              const sortedLifeCheckList = [...lifeCheckResponse.data].sort(
                (a, b) => (b.sequence || 0) - (a.sequence || 0),
              );
              console.log('取得したLifeCheckレコード:', sortedLifeCheckList);
              setLifeCheckDataList(sortedLifeCheckList);

              // 評価データに選択されたレコードがある場合はそれを選択
              if (evaluationData?.selected_record_type === 'lifecheck' && evaluationData?.selected_record_id) {
                const selectedData = sortedLifeCheckList.find(
                  (item: LifecheckData) => item.sequence?.toString() === evaluationData.selected_record_id,
                );
                if (selectedData) {
                  setSelectedLifeCheckData(selectedData);
                  setSelectedRecordId(evaluationData.selected_record_id);
                }
              } else if (evaluationData?.relation_lifecheck_sequence) {
                // relation_lifecheck_sequenceがある場合はそれを使用して選択
                const selectedData = sortedLifeCheckList.find(
                  (item: LifecheckData) =>
                    item.sequence?.toString() === evaluationData.relation_lifecheck_sequence.toString(),
                );
                if (selectedData) {
                  setSelectedRecordType('lifecheck');
                  setSelectedLifeCheckData(selectedData);
                  setSelectedRecordId(selectedData.sequence?.toString() || '');
                }
              } else if (sortedLifeCheckList.length > 0 && selectedRecordType === 'lifecheck' && !selectedRecordId) {
                // デフォルトで最新のデータを選択
                const latestLifeCheckData = sortedLifeCheckList[0];
                setSelectedLifeCheckData(latestLifeCheckData);
                setSelectedRecordId(latestLifeCheckData.sequence?.toString() || '');
              }
            } else {
              console.warn('LifeCheckレコードが見つかりませんでした');
            }
          } catch (error) {
            console.error('LifeCheckデータの取得に失敗しました:', error);
          }
        }
      } catch (error) {
        console.error('データ取得中にエラーが発生しました:', error);
      }
    };

    fetchBarthelAndLifeCheckData();
  }, [userId, evaluationData]);

  // レコードタイプが変更されたときの処理
  useEffect(() => {
    // レコードタイプが変更されたら、選択されたレコードIDをリセット
    setSelectedRecordId('');
    setSelectedBarthelData(null);
    setSelectedLifeCheckData(null);

    // デフォルトで最新のデータを選択
    if (selectedRecordType === 'barthel' && barthelDataList.length > 0) {
      // barthelDataListは既に日付降順でソート済みなので、先頭が最新
      const latestData = barthelDataList[0];
      setSelectedBarthelData(latestData);
      setSelectedRecordId(latestData.created_at || '');
    } else if (selectedRecordType === 'lifecheck' && lifeCheckDataList.length > 0) {
      // lifeCheckDataListは既に日付降順でソート済みなので、先頭が最新
      const latestData = lifeCheckDataList[0];
      setSelectedLifeCheckData(latestData);
      setSelectedRecordId(latestData.sequence?.toString() || '');
    }
  }, [selectedRecordType, barthelDataList, lifeCheckDataList]);

  // 選択されたレコードが変更されたときの処理
  const handleRecordChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const recordId = e.target.value as string;
    setSelectedRecordId(recordId);

    if (selectedRecordType === 'barthel') {
      const selectedData = barthelDataList.find((item: BarthelIndexData) => item.created_at === recordId);
      setSelectedBarthelData(selectedData || null);
    } else if (selectedRecordType === 'lifecheck') {
      const selectedData = lifeCheckDataList.find((item: LifecheckData) => item.sequence?.toString() === recordId);
      setSelectedLifeCheckData(selectedData || null);
    }
  };

  // フォームデータを取得する関数
  const handleGetFormData = () => {
    // 評価日のバリデーション
    if (!evaluate_date_year || !evaluate_date_month || !evaluate_date_day) {
      alert('評価日（年・月・日）は必須項目です。入力してください。');
      // 評価日のタブに切り替え
      setSelectedTab(0);
      return;
    }

    // 日付の妥当性チェック
    const year = parseInt(evaluate_date_year, 10);
    const month = parseInt(evaluate_date_month, 10) - 1; // JavaScriptの月は0から始まる
    const day = parseInt(evaluate_date_day, 10);
    const date = new Date(year, month, day);

    // 指定した日付が実際に存在するかチェック
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      alert('評価日が正しくありません。有効な日付を入力してください。');
      // 評価日のタブに切り替え
      setSelectedTab(0);
      return;
    }

    // 身長のバリデーション
    if (height) {
      const heightValue = parseInt(height, 10);
      if (isNaN(heightValue) || heightValue < 0 || heightValue > 999) {
        alert('身長は0から999の間の数値を入力してください。');
        // 必須項目のタブに切り替え
        setSelectedTab(0);
        return;
      }
    }

    // 体重のバリデーション
    if (weight) {
      const weightValue = parseInt(weight, 10);
      if (isNaN(weightValue) || weightValue < 0 || weightValue > 999) {
        alert('体重は0から999の間の数値を入力してください。');
        // 必須項目のタブに切り替え
        setSelectedTab(0);
        return;
      }
    }

    // フォームの値をリクエストボディとしてまとめる
    const formData: any = {
      evaluate_date_year,
      evaluate_date_month,
      evaluate_date_day,
      status,
      // ADLの項目（レコード選択情報）
      adl_relation_type: selectedRecordType,
      adl_relation_selected_id: selectedRecordId,
      // 他の項目
      height: height ? parseInt(height, 10) : null,
      weight: weight ? parseInt(weight, 10) : null,
      denture,
      choke,
      stains_on_teeth,
      condition_of_gums,
      diagnosis_of_dementia_01,
      diagnosis_of_dementia_02,
      diagnosis_of_dementia_03,
      diagnosis_of_dementia_evaluate_1_1,
      diagnosis_of_dementia_evaluate_1_2,
      diagnosis_of_dementia_evaluate_2,
      diagnosis_of_dementia_evaluate_3,
      diagnosis_of_dementia_evaluate_4,
      diagnosis_of_dementia_evaluate_5,
      diagnosis_of_dementia_evaluate_6,
      vitality_index_01,
      vitality_index_02,
      vitality_index_03,
      vitality_index_04,
      vitality_index_05,
      dbd13_01,
      dbd13_02,
      dbd13_03,
      dbd13_04,
      dbd13_05,
      dbd13_06,
      dbd13_07,
      dbd13_08,
      dbd13_09,
      dbd13_10,
      dbd13_11,
      dbd13_12,
      dbd13_13,
      basic_operation,
      walking_moving,
      cognitive_orientation,
      cognitive_communication,
      cognitive_mental_activity,
      swallowing_function,
      eating_assistance,
      excretion,
      bathing,
      oral_care,
      dressing,
      dressing_action,
      leisure,
      communication,
    };

    // ADLデータをリクエストボディに追加
    if (selectedRecordType === 'lifecheck' && selectedLifeCheckData) {
      // 生活機能チェックの場合
      formData.adl_relation_unique_id = selectedLifeCheckData.unique_id;

      // LifeCheckのレベル値をBarthelIndexの値に変換して追加
      // 食事
      if (selectedLifeCheckData.adl_feeding?.level && selectedLifeCheckData.adl_feeding.level.length > 0) {
        formData.barthel_index_meal =
          selectedLifeCheckData.adl_feeding.level[0] === 0
            ? 2
            : selectedLifeCheckData.adl_feeding.level[0] === 1
            ? 1
            : 0;
      }

      // 椅子とベッド間の移乗
      if (selectedLifeCheckData.adl_transfers?.level && selectedLifeCheckData.adl_transfers.level.length > 0) {
        formData.barthel_transfer =
          selectedLifeCheckData.adl_transfers.level[0] === 0
            ? 3
            : selectedLifeCheckData.adl_transfers.level[0] === 1
            ? 2
            : selectedLifeCheckData.adl_transfers.level[0] === 2
            ? 1
            : 0;
      }

      // 整容
      if (selectedLifeCheckData.adl_grooming?.level && selectedLifeCheckData.adl_grooming.level.length > 0) {
        formData.barthel_index_personal_hygiene_and_adjustment =
          selectedLifeCheckData.adl_grooming.level[0] === 0
            ? 2
            : selectedLifeCheckData.adl_grooming.level[0] === 1
            ? 1
            : 0;
      }

      // トイレ動作
      if (selectedLifeCheckData.adl_toilet_use?.level && selectedLifeCheckData.adl_toilet_use.level.length > 0) {
        formData.barthel_index_toilet_activity =
          selectedLifeCheckData.adl_toilet_use.level[0] === 0
            ? 2
            : selectedLifeCheckData.adl_toilet_use.level[0] === 1
            ? 1
            : 0;
      }

      // 入浴
      if (selectedLifeCheckData.adl_bathing?.level && selectedLifeCheckData.adl_bathing.level.length > 0) {
        formData.barthel_index_bathing =
          selectedLifeCheckData.adl_bathing.level[0] === 0
            ? 2
            : selectedLifeCheckData.adl_bathing.level[0] === 1
            ? 1
            : 0;
      }

      // 平地歩行
      if (selectedLifeCheckData.adl_mobility?.level && selectedLifeCheckData.adl_mobility.level.length > 0) {
        formData.barthel_index_flat_ground_walking =
          selectedLifeCheckData.adl_mobility.level[0] === 0
            ? 3
            : selectedLifeCheckData.adl_mobility.level[0] === 1
            ? 2
            : selectedLifeCheckData.adl_mobility.level[0] === 2
            ? 1
            : 0;
      }

      // 階段昇降
      if (selectedLifeCheckData.adl_stairs?.level && selectedLifeCheckData.adl_stairs.level.length > 0) {
        formData.barthel_index_stair_movement =
          selectedLifeCheckData.adl_stairs.level[0] === 0 ? 2 : selectedLifeCheckData.adl_stairs.level[0] === 1 ? 1 : 0;
      }

      // 更衣
      if (selectedLifeCheckData.adl_dressing?.level && selectedLifeCheckData.adl_dressing.level.length > 0) {
        formData.barthel_index_changing_clothes =
          selectedLifeCheckData.adl_dressing.level[0] === 0
            ? 2
            : selectedLifeCheckData.adl_dressing.level[0] === 1
            ? 1
            : 0;
      }

      // 排便コントロール
      if (selectedLifeCheckData.adl_bowels?.level && selectedLifeCheckData.adl_bowels.level.length > 0) {
        formData.barthel_index_defecation_manage =
          selectedLifeCheckData.adl_bowels.level[0] === 0 ? 2 : selectedLifeCheckData.adl_bowels.level[0] === 1 ? 1 : 0;
      }

      // 排尿コントロール
      if (selectedLifeCheckData.adl_bladder?.level && selectedLifeCheckData.adl_bladder.level.length > 0) {
        formData.barthel_index_urination_manage =
          selectedLifeCheckData.adl_bladder.level[0] === 0
            ? 2
            : selectedLifeCheckData.adl_bladder.level[0] === 1
            ? 1
            : 0;
      }
    } else if (selectedRecordType === 'barthel' && selectedBarthelData) {
      // バーセルインデックスの場合
      formData.adl_relation_unique_id = selectedBarthelData.unique_id;

      // Barthel Indexの値を直接追加
      // 食事
      if (selectedBarthelData.feeding) {
        const feedingValue = parseInt(selectedBarthelData.feeding, 10);
        formData.barthel_index_meal = feedingValue === 10 ? 2 : feedingValue === 5 ? 1 : 0;
      }

      // 椅子とベッド間の移乗
      if (selectedBarthelData.transfers) {
        const transfersValue = parseInt(selectedBarthelData.transfers, 10);
        formData.barthel_transfer =
          transfersValue === 15 ? 3 : transfersValue === 10 ? 2 : transfersValue === 5 ? 1 : 0;
      }

      // 整容
      if (selectedBarthelData.grooming) {
        const groomingValue = parseInt(selectedBarthelData.grooming, 10);
        formData.barthel_index_personal_hygiene_and_adjustment = groomingValue === 5 ? 2 : groomingValue === 0 ? 0 : 1;
      }

      // トイレ動作
      if (selectedBarthelData.toilet_use) {
        const toiletUseValue = parseInt(selectedBarthelData.toilet_use, 10);
        formData.barthel_index_toilet_activity = toiletUseValue === 10 ? 2 : toiletUseValue === 5 ? 1 : 0;
      }

      // 入浴
      if (selectedBarthelData.bathing) {
        const bathingValue = parseInt(selectedBarthelData.bathing, 10);
        formData.barthel_index_bathing = bathingValue === 5 ? 2 : bathingValue === 0 ? 0 : 1;
      }

      // 平地歩行
      if (selectedBarthelData.mobility) {
        const mobilityValue = parseInt(selectedBarthelData.mobility, 10);
        formData.barthel_index_flat_ground_walking =
          mobilityValue === 15 ? 3 : mobilityValue === 10 ? 2 : mobilityValue === 5 ? 1 : 0;
      }

      // 階段昇降
      if (selectedBarthelData.stairs) {
        const stairsValue = parseInt(selectedBarthelData.stairs, 10);
        formData.barthel_index_stair_movement = stairsValue === 10 ? 2 : stairsValue === 5 ? 1 : 0;
      }

      // 更衣
      if (selectedBarthelData.dressing) {
        const dressingValue = parseInt(selectedBarthelData.dressing, 10);
        formData.barthel_index_changing_clothes = dressingValue === 10 ? 2 : dressingValue === 5 ? 1 : 0;
      }

      // 排便コントロール
      if (selectedBarthelData.bowels) {
        const bowelsValue = parseInt(selectedBarthelData.bowels, 10);
        formData.barthel_index_defecation_manage = bowelsValue === 10 ? 2 : bowelsValue === 5 ? 1 : 0;
      }

      // 排尿コントロール
      if (selectedBarthelData.bladder) {
        const bladderValue = parseInt(selectedBarthelData.bladder, 10);
        formData.barthel_index_urination_manage = bladderValue === 10 ? 2 : bladderValue === 5 ? 1 : 0;
      }
    }

    return formData;
  };

  // 保存処理
  const handleSave = async () => {
    // 親コンポーネントの保存処理を呼び出す
    onSave(handleGetFormData());
  };

  // 日付をフォーマットする関数
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return moment(dateString).format('YYYY/MM/DD');
  };

  // LifeCheckのレベル値を文字列に変換する関数
  const formatLifeCheckLevel = (level?: number[], type?: string) => {
    if (!level || level.length === 0) return '未評価';

    // levelの最初の値を取得
    const levelValue = level[0];

    switch (type) {
      case 'feeding': // 食事
        switch (levelValue) {
          case 0:
            return '自立(10)';
          case 1:
            return '一部介助(5)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'transfers': // 椅子とベッド間の移乗
        switch (levelValue) {
          case 0:
            return '自立(15)';
          case 1:
            return '監視下(10)';
          case 2:
            return '座れるが移れない(5)';
          case 3:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'grooming': // 整容
        switch (levelValue) {
          case 0:
            return '自立(5)';
          case 1:
            return '一部介助(0)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'toilet_use': // トイレ動作
        switch (levelValue) {
          case 0:
            return '自立(10)';
          case 1:
            return '一部介助(5)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'bathing': // 入浴
        switch (levelValue) {
          case 0:
            return '自立(5)';
          case 1:
            return '一部介助(0)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'mobility': // 平地歩行
        switch (levelValue) {
          case 0:
            return '自立(15)';
          case 1:
            return '歩行器等(10)';
          case 2:
            return '車椅子操作が可能(5)';
          case 3:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'stairs': // 階段昇降
        switch (levelValue) {
          case 0:
            return '自立(10)';
          case 1:
            return '一部介助(5)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'dressing': // 更衣
        switch (levelValue) {
          case 0:
            return '自立(10)';
          case 1:
            return '一部介助(5)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'bowels': // 排便コントロール
        switch (levelValue) {
          case 0:
            return '自立(10)';
          case 1:
            return '一部介助(5)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      case 'bladder': // 排尿コントロール
        switch (levelValue) {
          case 0:
            return '自立(10)';
          case 1:
            return '一部介助(5)';
          case 2:
            return '全介助(0)';
          default:
            return level.join(', ');
        }
      default:
        return level.join(', ');
    }
  };

  // Barthelの値を表示用に変換する関数
  const formatBarthelValue = (value?: string, type?: string) => {
    if (!value) return '未評価';

    // 数値に変換
    const numValue = parseInt(value, 10);

    switch (type) {
      case 'feeding': // 食事
        switch (numValue) {
          case 10:
            return '自立(10)';
          case 5:
            return '一部介助(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'transfers': // 椅子とベッド間の移乗
        switch (numValue) {
          case 15:
            return '自立(15)';
          case 10:
            return '一部介助(10)';
          case 5:
            return '一部介助（座れるが移れない）(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'grooming': // 整容
        switch (numValue) {
          case 5:
            return '自立(5)';
          case 0:
            return '介助が必要(0)';
          default:
            return `${value}`;
        }
      case 'toilet_use': // トイレ動作
        switch (numValue) {
          case 10:
            return '自立(10)';
          case 5:
            return '一部介助(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'bathing': // 入浴
        switch (numValue) {
          case 5:
            return '自立(5)';
          case 0:
            return '介助が必要(0)';
          default:
            return `${value}`;
        }
      case 'mobility': // 平地歩行
        switch (numValue) {
          case 15:
            return '自立(15)';
          case 10:
            return '一部介助(10)';
          case 5:
            return '一部介助（車椅子操作が可能）(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'stairs': // 階段昇降
        switch (numValue) {
          case 10:
            return '自立(10)';
          case 5:
            return '一部介助(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'dressing': // 更衣
        switch (numValue) {
          case 10:
            return '自立(10)';
          case 5:
            return '一部介助(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'bowels': // 排便コントロール
        switch (numValue) {
          case 10:
            return '自立(10)';
          case 5:
            return '一部介助(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      case 'bladder': // 排尿コントロール
        switch (numValue) {
          case 10:
            return '自立(10)';
          case 5:
            return '一部介助(5)';
          case 0:
            return '全介助(0)';
          default:
            return `${value}`;
        }
      default:
        return `${value}`;
    }
  };

  return (
    <Box component="article" className={classes.root}>
      <Box className={classes.headerTitle}>科学的介護推進に関する評価</Box>
      <Box component="section" m="30px 0 0 0" p="30px 25px 0">
        <Grid container spacing={3}>
          <Grid item container xs={12}>
            <Grid item xs={12} sm={2}>
              <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                <GeneralPrimaryGhostButton
                  onClick={onCancel}
                  innerText={'保存せず戻る'}
                  setCss={'width: 120px; height: 40px;'}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box width={{ xs: '100%', sm: '200px' }}>
                <GeneralPrimaryButton txt="保存する" setCss={'width: 100px; height: 40px;'} onClick={handleSave} />
              </Box>
            </Grid>
            {/* 編集モードの場合のみ表示する */}
            {isEditMode && onDuplicate && (
              <Grid item xs={12} sm={2}>
                <Box width={{ xs: '100%', sm: '200px' }}>
                  <GeneralPrimaryGhostButton
                    onClick={() => onDuplicate(handleGetFormData())}
                    innerText={'複製する'}
                    setCss={'width: 100px; height: 40px;'}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={3}></Grid>
            {/* 編集モードの場合のみ表示する */}
            {isEditMode && onDelete && (
              <Grid item xs={12} sm={2}>
                <Box width={{ xs: '100%', sm: '200px' }}>
                  <GeneralPrimaryGhostButton
                    onClick={onDelete}
                    innerText={'削除する'}
                    setCss={'width: 100px; height: 40px;'}
                  />
                </Box>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <TabSelector selectedTab={selectedTab} tabLabels={tabLabels} onTabChange={handleTabChange} />
          </Grid>

          {selectedTab === 0 && (
            <Grid item xs={12} className={classes.tabContent}>
              {/* 必須項目のコンテンツ */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  評価日
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <TextField
                      select
                      label="年"
                      value={evaluate_date_year}
                      onChange={(e) => setEvaluateDateYear(e.target.value)}
                      className={classes.selectField}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value=""></option>
                      {generateYearOptions()}
                    </TextField>
                  </Grid>
                  <Grid item>
                    <TextField
                      select
                      label="月"
                      value={evaluate_date_month}
                      onChange={(e) => setEvaluateDateMonth(e.target.value)}
                      className={classes.selectField}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value=""></option>
                      {generateMonthOptions()}
                    </TextField>
                  </Grid>
                  <Grid item>
                    <TextField
                      select
                      label="日"
                      value={evaluate_date_day}
                      onChange={(e) => setEvaluateDateDay(e.target.value)}
                      className={classes.selectField}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value=""></option>
                      {generateDayOptions()}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  利用者情報
                </Typography>
                <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                  評価時点
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="service-status"
                    name="service-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    row
                  >
                    <FormControlLabel value="1" control={<Radio color="primary" />} label="サービス開始時" />
                    <FormControlLabel value="2" control={<Radio color="primary" />} label="サービス利用中" />
                    <FormControlLabel value="3" control={<Radio color="primary" />} label="サービス利用終了時" />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ADL（日常生活動作）
                </Typography>

                {/* レコード選択部分 */}
                <Grid container spacing={3} className={classes.recordSelector}>
                  <Grid item xs={12} sm={6}>
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        aria-label="record-type"
                        name="record-type"
                        value={selectedRecordType}
                        onChange={(e) => setSelectedRecordType(e.target.value)}
                        row
                      >
                        <FormControlLabel
                          value="lifecheck"
                          control={<Radio color="primary" />}
                          label="生活機能チェックアプリ"
                        />
                        <FormControlLabel
                          value="barthel"
                          control={<Radio color="primary" />}
                          label="モフトレBIアプリ"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="record-select-label">レコード選択</InputLabel>
                      <Select
                        labelId="record-select-label"
                        id="record-select"
                        value={selectedRecordId}
                        onChange={handleRecordChange}
                      >
                        {selectedRecordType === 'barthel' && barthelDataList.length > 0 ? (
                          barthelDataList.map((item, index) => (
                            <MenuItem key={`barthel-${index}`} value={item.created_at}>
                              {formatDate(item.created_at)}
                            </MenuItem>
                          ))
                        ) : selectedRecordType === 'barthel' ? (
                          <MenuItem value="">
                            <em>データがありません</em>
                          </MenuItem>
                        ) : null}
                        {selectedRecordType === 'lifecheck' && lifeCheckDataList.length > 0 ? (
                          lifeCheckDataList.map((item) => (
                            <MenuItem key={item.sequence} value={item.sequence?.toString()}>
                              {formatDate(item.visited_time_start)} (No.{item.sequence})
                            </MenuItem>
                          ))
                        ) : selectedRecordType === 'lifecheck' ? (
                          <MenuItem value="">
                            <em>データがありません</em>
                          </MenuItem>
                        ) : null}
                      </Select>
                      {selectedRecordType === 'barthel' && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            Barthel Indexデータ件数: {barthelDataList.length}
                          </Typography>
                        </Box>
                      )}
                      {selectedRecordType === 'lifecheck' && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            LifeCheckデータ件数: {lifeCheckDataList.length}
                          </Typography>
                        </Box>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                {/* ADL項目表示部分 */}
                {selectedBarthelData || selectedLifeCheckData ? (
                  <Grid container spacing={1}>
                    {/* 食事 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          食事
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.feeding, 'feeding')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_feeding?.level, 'feeding')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 椅子とベッド間の移乗 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          椅子とベッド間の移乗
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.transfers, 'transfers')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_transfers?.level, 'transfers')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 整容 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          整容
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.grooming, 'grooming')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_grooming?.level, 'grooming')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* トイレ動作 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          トイレ動作
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.toilet_use, 'toilet_use')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_toilet_use?.level, 'toilet_use')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 入浴 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          入浴
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.bathing, 'bathing')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_bathing?.level, 'bathing')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 平地歩行 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          平地歩行
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.mobility, 'mobility')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_mobility?.level, 'mobility')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 階段昇降 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          階段昇降
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.stairs, 'stairs')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_stairs?.level, 'stairs')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 更衣 */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          更衣
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.dressing, 'dressing')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_dressing?.level, 'dressing')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 排便コントロール */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          排便コントロール
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.bowels, 'bowels')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_bowels?.level, 'bowels')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* 排尿コントロール */}
                    <Grid item xs={12} sm={6} className={classes.formField}>
                      <Paper className={classes.adlItem}>
                        <Typography variant="subtitle1" className={classes.adlTitle}>
                          排尿コントロール
                        </Typography>
                        {selectedRecordType === 'barthel' && selectedBarthelData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatBarthelValue(selectedBarthelData.bladder, 'bladder')}
                            </Typography>
                          </Box>
                        )}
                        {selectedRecordType === 'lifecheck' && selectedLifeCheckData && (
                          <Box className={classes.adlData}>
                            <Typography variant="body2" className={classes.adlDataSource}>
                              {formatLifeCheckLevel(selectedLifeCheckData.adl_bladder?.level, 'bladder')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                ) : (
                  <Box className={classes.noData}>
                    <Typography variant="body1">レコードが選択されていないか、データが存在しません。</Typography>
                  </Box>
                )}
              </Box>

              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  口腔・栄養
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} className={classes.formField}>
                    <TextField
                      label="身長"
                      type="number"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className={classes.numberInput}
                      InputProps={{
                        endAdornment: <span>cm</span>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.formField}>
                    <TextField
                      label="体重"
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className={classes.numberInput}
                      InputProps={{
                        endAdornment: <span>kg</span>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.formField}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                        義歯の使用
                      </Typography>
                      <RadioGroup
                        aria-label="denture"
                        name="denture"
                        value={denture}
                        onChange={(e) => setDenture(e.target.value)}
                        row
                      >
                        <FormControlLabel value="1" control={<Radio color="primary" />} label="あり" />
                        <FormControlLabel value="0" control={<Radio color="primary" />} label="なし" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.formField}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                        むせ
                      </Typography>
                      <RadioGroup
                        aria-label="choking"
                        name="choking"
                        value={choke}
                        onChange={(e) => setChoke(e.target.value)}
                        row
                      >
                        <FormControlLabel value="1" control={<Radio color="primary" />} label="あり" />
                        <FormControlLabel value="0" control={<Radio color="primary" />} label="なし" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.formField}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                        歯の汚れ
                      </Typography>
                      <RadioGroup
                        aria-label="tooth-dirt"
                        name="tooth-dirt"
                        value={stains_on_teeth}
                        onChange={(e) => setStainsOnTeeth(e.target.value)}
                        row
                      >
                        <FormControlLabel value="1" control={<Radio color="primary" />} label="あり" />
                        <FormControlLabel value="0" control={<Radio color="primary" />} label="なし" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} className={classes.formField}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                        歯肉の腫れ・出血
                      </Typography>
                      <RadioGroup
                        aria-label="gum-problem"
                        name="gum-problem"
                        value={condition_of_gums}
                        onChange={(e) => setConditionOfGums(e.target.value)}
                        row
                      >
                        <FormControlLabel value="1" control={<Radio color="primary" />} label="あり" />
                        <FormControlLabel value="0" control={<Radio color="primary" />} label="なし" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  認知症
                </Typography>
                <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                  認知症の診断
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={diagnosis_of_dementia_01}
                          onChange={(e) => setDiagnosisOfDementia01(e.target.checked)}
                          name="alzheimer"
                          color="primary"
                        />
                      }
                      label="アルツハイマー病"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={diagnosis_of_dementia_02}
                          onChange={(e) => setDiagnosisOfDementia02(e.target.checked)}
                          name="vascular-dementia"
                          color="primary"
                        />
                      }
                      label="血管性認知症"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={diagnosis_of_dementia_03}
                          onChange={(e) => setDiagnosisOfDementia03(e.target.checked)}
                          name="lewy-body"
                          color="primary"
                        />
                      }
                      label="レビー小体病"
                    />
                  </FormGroup>
                </FormControl>
              </Box>

              {/* ①-1 身近なものを置いた場所を覚えているか */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ①-1 身近なもの（たとえば、メガネや入れ歯、財布、上着、鍵など）を置いた場所を覚えていますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※介護者が一緒に探しているなど、一人で探す様子が分からない場合は、もし一人で探すとしたらどうかを想定して評価してください
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="memory-item"
                    name="memory-item"
                    value={diagnosis_of_dementia_evaluate_1_1}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate1_1(e.target.value)}
                  >
                    <FormControlLabel value="5" control={<Radio color="primary" />} label="５：常に覚えている" />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：たまに（週１回程度）忘れることはあるが、考えることで思い出せる"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：思い出せないこともあるが、きっかけがあれば自分で思い出すこともある（思い出せることと思い出せないことが同じくらいの頻度）"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：きっかけがあっても、自分では置いた場所をほとんど思い出せない"
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="１：忘れたこと自体を認識していない"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ①-2 日常的な出来事をどのくらい覚えているか */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ①-2
                  身の回りに起こった日常的な出来事（たとえば、食事、入浴、リハビリテーションや外出など）をどのくらいの期間、覚えていますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※最近1週間の様子を評価してください
                  <br />
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="memory-event"
                    name="memory-event"
                    value={diagnosis_of_dementia_evaluate_1_2}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate1_2(e.target.value)}
                  >
                    <FormControlLabel
                      value="5"
                      control={<Radio color="primary" />}
                      label="５：１週間前のことを覚えている"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：１週間前のことは覚えていないが、数日前のことは覚えている"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：数日前のことは覚えていないが、昨日のことは覚えている"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：昨日のことは覚えていないが、半日前のことは覚えている"
                    />
                    <FormControlLabel value="1" control={<Radio color="primary" />} label="１：全く覚えていられない" />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ② 現在の日付や場所等の認識 */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ② 現在の日付や場所等についてどの程度認識できますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※上位レベルのことと下位レベルのことが両方でき、上位と下位の間の項目ができない場合には、上位レベルのほうを選び回答してください
                  <br />
                  例：１と３に該当し、２に該当しない場合⇒３を選択する
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="awareness"
                    name="awareness"
                    value={diagnosis_of_dementia_evaluate_2}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate2(e.target.value)}
                  >
                    <FormControlLabel
                      value="5"
                      control={<Radio color="primary" />}
                      label="５：年月日はわかる（±１日の誤差は許容する）"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：年月日はわからないが、現在いる場所の種類はわかる"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：場所の名称や種類はわからないが、その場にいる人が誰だかわかる（家族であるか、介護者であるか、看護師であるか等）"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：その場にいる人が誰だかわからないが、自分の名前はわかる"
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="１：自分の名前がわからない"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ③ 会話でのコミュニケーション */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ③ 誰かに何かを伝えたいと思っているとき、どれくらい会話でそれを伝えることができますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※「会話ができる」とは、2 者の意思が互いに疎通できている状態を指します
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="communication"
                    name="communication"
                    value={diagnosis_of_dementia_evaluate_3}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate3(e.target.value)}
                  >
                    <FormControlLabel
                      value="5"
                      control={<Radio color="primary" />}
                      label="５：会話に支障がない（「○○だから、××である　」といった2つ以上の情報がつながった話をすることができる　）"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：複雑な会話はできないが、普通に会話はできる（「○○だから、××である　」といった2つ以上の情報がつながった話をすることはできない　）"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：普通に会話はできないが、具体的な欲求を伝えることはできる（「痛い」「お腹が空いた」などの具体的な要求しか伝えられない　）"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：会話が成り立たないが、発語はある（発語はあるが、簡単な質問に対して適切な回答ができなかったり、何を聞いても「うん」とだけ答える　）"
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="１：発語がなく、無言である"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ④ 服薬 */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ④ 一人で服薬ができますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※服薬していなかったり、介護者が先に準備しているなど、実際の服薬能力が分からない場合は、一人で服薬する場合を想定して評価してください
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="medication"
                    name="medication"
                    value={diagnosis_of_dementia_evaluate_4}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate4(e.target.value)}
                  >
                    <FormControlLabel
                      value="5"
                      control={<Radio color="primary" />}
                      label="５：自分で正しく服薬できる"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：自分で用意して服薬できるが、たまに（週１回程度）服薬し忘れることがある"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：２回に１回は服薬を忘れる"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：常に薬を手渡しすることが必要である"
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="１：服薬し終わるまで介助・みまもりが必要である"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ⑤ 着替え */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ⑤ 一人で着替えることができますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※まひ等により身体が不自由で介助が必要な場合は、障害がない場合での衣服の機能への理解度を想定して評価してください
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="dressing"
                    name="dressing"
                    value={diagnosis_of_dementia_evaluate_5}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate5(e.target.value)}
                  >
                    <FormControlLabel
                      value="5"
                      control={<Radio color="primary" />}
                      label="５：季節や気温に応じた服装を選び、着脱衣ができる"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：季節や気温に応じた服装選びはできないが、着る順番や方法は理解し、自分で着脱衣ができる"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：促してもらえれば、自分で着脱衣ができる"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：着脱衣の一部を介護者が行う必要がある"
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="１：着脱衣の全てを常に介護者が行う必要がある"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ⑥ 電化製品の操作 */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  ⑥ テレビやエアコンなどの電化製品を操作できますか
                </Typography>
                <Typography
                  variant="caption"
                  gutterBottom
                  style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}
                >
                  ※テレビが無い場合は、エアコンで評価してください。いずれもない場合は、電子レンジ、ラジオなどの電化製品の操作で評価してください
                </Typography>
                <FormControl component="fieldset" className={classes.formField}>
                  <RadioGroup
                    aria-label="appliance"
                    name="appliance"
                    value={diagnosis_of_dementia_evaluate_6}
                    onChange={(e) => setDiagnosisOfDementiaEvaluate6(e.target.value)}
                  >
                    <FormControlLabel
                      value="5"
                      control={<Radio color="primary" />}
                      label="５：自由に操作できる（「複雑な操作」も自分で考えて行うことができる　）"
                    />
                    <FormControlLabel
                      value="4"
                      control={<Radio color="primary" />}
                      label="４：チャンネルの順送りなど普段している操作はできる（「単純な操作」であれば自分で行うことができる　）"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio color="primary" />}
                      label="３：操作間違いが多いが、操作方法を教えてもらえれば使える（「単純な操作」が分からないことがあるが、教えれば自分で操作することができる　）"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="２：リモコンを認識しているが、リモコンの使い方が全く分からない（何をする電化製品かは分かるが、操作を教えても自分で操作することはできない　）"
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="１：リモコンが何をするものか分からない"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Vitality index */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  Vitality index
                </Typography>

                {/* 意思疎通 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    意思疎通
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="communication-type"
                      name="communication-type"
                      value={vitality_index_01}
                      onChange={(e) => setVitalityIndex01(e.target.value)}
                    >
                      <FormControlLabel
                        value="2"
                        control={<Radio color="primary" />}
                        label="２：自分から挨拶する、話し掛ける"
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio color="primary" />}
                        label="１：挨拶、呼びかけに対して返答や笑顔がみられる"
                      />
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：反応がない" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* 起床 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    起床
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="wakeup-type"
                      name="wakeup-type"
                      value={vitality_index_02}
                      onChange={(e) => setVitalityIndex02(e.target.value)}
                    >
                      <FormControlLabel
                        value="2"
                        control={<Radio color="primary" />}
                        label="２：いつも定時に起床している"
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio color="primary" />}
                        label="１：起こさないと起床しないことがある"
                      />
                      <FormControlLabel
                        value="0"
                        control={<Radio color="primary" />}
                        label="０：自分から起床することはない"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* 食事 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    食事
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="eating-type"
                      name="eating-type"
                      value={vitality_index_03}
                      onChange={(e) => setVitalityIndex03(e.target.value)}
                    >
                      <FormControlLabel
                        value="2"
                        control={<Radio color="primary" />}
                        label="２：自分から進んで食べようとする"
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio color="primary" />}
                        label="１：促されると食べようとする"
                      />
                      <FormControlLabel
                        value="0"
                        control={<Radio color="primary" />}
                        label="０：食事に関心がない、全く食べようとしない"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* 排せつ */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    排せつ
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="toileting-type"
                      name="toileting-type"
                      value={vitality_index_04}
                      onChange={(e) => setVitalityIndex04(e.target.value)}
                    >
                      <FormControlLabel
                        value="2"
                        control={<Radio color="primary" />}
                        label="２：いつも自ら便意尿意を伝える、あるいは自分で排尿、排便を行う"
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio color="primary" />}
                        label="１：時々、尿意便意を伝える"
                      />
                      <FormControlLabel
                        value="0"
                        control={<Radio color="primary" />}
                        label="０：排せつに全く関心がない"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* リハビリ・活動 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    リハビリ・活動
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="activity-type"
                      name="activity-type"
                      value={vitality_index_05}
                      onChange={(e) => setVitalityIndex05(e.target.value)}
                    >
                      <FormControlLabel
                        value="2"
                        control={<Radio color="primary" />}
                        label="２：自らリハビリに向かう、活動を求める"
                      />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：促されて向かう" />
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：拒否、無関心" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
          )}

          {selectedTab === 1 && (
            <Grid item xs={12} className={classes.tabContent}>
              {/* 任意項目のコンテンツ */}
              <Box className={classes.formSection}>
                <Typography variant="h6" className={classes.formTitle}>
                  【DBD13】
                </Typography>

                {/* DBD13 項目1 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    １ 忘れてしまうことが多いため，同じことを何度も聞いてしまう
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd1"
                      name="dbd1"
                      value={dbd13_01}
                      onChange={(e) => setDbd13_01(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目2 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ２ よく物をなくしたり，置場所を間違えたりする
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd2"
                      name="dbd2"
                      value={dbd13_02}
                      onChange={(e) => setDbd13_02(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目3 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ３ 日常的な物事に関心を持てない
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd3"
                      name="dbd3"
                      value={dbd13_03}
                      onChange={(e) => setDbd13_03(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目4 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ４ 特別な理由がないのに夜中に起きて布団から出てしまう
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd4"
                      name="dbd4"
                      value={dbd13_04}
                      onChange={(e) => setDbd13_04(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目5 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ５ 他人が納得できる根拠がない状況で，他人に文句を言ってしまう
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd5"
                      name="dbd5"
                      value={dbd13_05}
                      onChange={(e) => setDbd13_05(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目6 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ６ 昼間，寝ていることが多い
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd6"
                      name="dbd6"
                      value={dbd13_06}
                      onChange={(e) => setDbd13_06(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目7 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ７ 過度に歩き回ることが多い
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd7"
                      name="dbd7"
                      value={dbd13_07}
                      onChange={(e) => setDbd13_07(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目8 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ８ 同じ動作を何回も繰り返してしまう
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd8"
                      name="dbd8"
                      value={dbd13_08}
                      onChange={(e) => setDbd13_08(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目9 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    ９ 荒い口調で相手を責めるような言葉を出してしまう
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd9"
                      name="dbd9"
                      value={dbd13_09}
                      onChange={(e) => setDbd13_09(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目10 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    10 服装が場違いな，あるいは季節に合わない場合がある
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd10"
                      name="dbd10"
                      value={dbd13_10}
                      onChange={(e) => setDbd13_10(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目11 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    11 世話をしてもらうことを受け入れられない
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd11"
                      name="dbd11"
                      value={dbd13_11}
                      onChange={(e) => setDbd13_11(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目12 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    12 周囲にわかってもらえるような理由なしに物を貯め込んでしまう
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd12"
                      name="dbd12"
                      value={dbd13_12}
                      onChange={(e) => setDbd13_12(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* DBD13 項目13 */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                    13 引き出しやたんすの物を取り出そうとして，中身を全部出してしまうことがある
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="dbd13"
                      name="dbd13"
                      value={dbd13_13}
                      onChange={(e) => setDbd13_13(e.target.value)}
                    >
                      <FormControlLabel value="0" control={<Radio color="primary" />} label="０：まったくない" />
                      <FormControlLabel value="1" control={<Radio color="primary" />} label="１：ほとんどない" />
                      <FormControlLabel value="2" control={<Radio color="primary" />} label="２：ときどきある" />
                      <FormControlLabel value="3" control={<Radio color="primary" />} label="３：よくある" />
                      <FormControlLabel value="4" control={<Radio color="primary" />} label="４：常にある" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Box className={classes.formSection}>
                  <Typography variant="h6" className={classes.formTitle}>
                    【ICFステージング】
                  </Typography>

                  {/* 基本動作 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      基本動作
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-basic-movement"
                        name="icf-basic-movement"
                        value={basic_operation}
                        onChange={(e) => setBasicOperation(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：両足での立位保持を行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：立位の保持は行っていないが、座位での乗り移りは行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：座位での乗り移りは行っていないが、座位（端座位）の保持は行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：座位（端座位）の保持は行っていないが、寝返りは行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：寝返りは行っていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 歩行・移動 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      歩行・移動
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-walking"
                        name="icf-walking"
                        value={walking_moving}
                        onChange={(e) => setWalkingMoving(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：公共交通機関等を利用した外出を行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：公共交通機関等を利用した外出は行っていないが、手すりに頼らないで安定した階段の昇り降りを行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：手すりに頼らない安定した階段の昇り降りを行っていないが、平らな場所での安定した歩行は行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：安定した歩行は行っていないが、施設内の移動は行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：施設内の移動を行っていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 認知機能 オリエンテーション（見当識） */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      認知機能。オリエンテーション（見当識）
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-orientation"
                        name="icf-orientation"
                        value={cognitive_orientation}
                        onChange={(e) => setCognitiveOrientation(e.target.value)}
                      >
                        <FormControlLabel value="5" control={<Radio color="primary" />} label="５：年月日がわかる" />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：年月日はわからないが、現在いる場所の種類はわかる"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：場所の名称や種類はわからないが、その場にいる人が誰だかわかる"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：その場にいる人が誰だかわからないが、自分の名前はわかる"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：自分の名前がわからない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 認知機能 コミュニケーション */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      認知機能。コミュニケーション
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-communication"
                        name="icf-communication"
                        value={cognitive_communication}
                        onChange={(e) => setCognitiveCommunication(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：複雑な人間関係を保っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：複雑な人間関係は保っていないが、書き言葉は理解している"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：書き言葉は理解していないが日常会話は行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：日常会話は行っていないが、話し言葉は理解している"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：話し言葉の理解はできない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 認知機能 精神活動 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      認知機能。精神活動
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-mental-activity"
                        name="icf-mental-activity"
                        value={cognitive_mental_activity}
                        onChange={(e) => setCognitiveMentalActivity(e.target.value)}
                      >
                        <FormControlLabel value="5" control={<Radio color="primary" />} label="５：時間管理ができる" />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：時間管理はできないが、簡単な算術計算はできる"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：簡単な算術計算はできないが、記憶の再生はできる"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２: 記憶の再生はできないが、意識混濁はない"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１: 意識の混濁があった"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 食事 嚥下機能 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      食事 嚥下機能
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-swallowing"
                        name="icf-swallowing"
                        value={swallowing_function}
                        onChange={(e) => setSwallowingFunction(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：肉などを含む普通の食事を、噛んで食べることを行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：肉などを含む普通の食事を噛んで食べることは行っていないが、ストローなどでむせずに飲むことは行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：むせずに吸引することは行っていないが、固形物の嚥下は行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：固形物の嚥下は行っていないが、嚥下食の嚥下は行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：嚥下食の嚥下を行っていない（食べ物の嚥下を行っていない）"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 食事 食事動作および食事介助 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      食事 食事動作および食事介助
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-eating-action"
                        name="icf-eating-action"
                        value={eating_assistance}
                        onChange={(e) => setEatingAssistance(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：箸やフォークを使って食べこぼしせず、上手に食べることを行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：箸やフォークを使って上手に食べることは行っていないが、食べこぼししながらも、何とか自分で食べることを行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：自分で食べることを行っていないが、食事の際に特別なセッティングをすれば自分で食べることを行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：食事の際に特別なセッティングをしても自分で食べることを行っていないが、直接的な介助があれば食べることを行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：直接的な介助をしても食べることを行っていない（食べることを行っていない）"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 排泄の動作 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      排泄の動作
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-toileting"
                        name="icf-toileting"
                        value={excretion}
                        onChange={(e) => setExcretion(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：排泄の後始末を行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：排泄の後始末は行っていないが、ズボン・パンツの上げ下ろしは行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：ズボン・パンツの上げ下ろしは行っていないが、洋式便器への移乗は行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：洋式トイレの移乗が自分で行えないため、介助が必要、または普段から床上で排泄を行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：尿閉（膀胱瘻を含む）や医療的な身体管理のために膀胱等へのカテーテルなどを使用している"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 入浴動作 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      入浴動作
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-bathing"
                        name="icf-bathing"
                        value={bathing}
                        onChange={(e) => setBathing(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：安定した浴槽の出入りと洗身を行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：安定した浴槽の出入りと洗身は行っていないが、第三者の援助なしで入浴を行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：第三者の援助なしで入浴することは行っていないが、一般浴室内での座位保持は行っている。その他、入浴に必要なさまざまな介助がなされている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２: 浴室内での座位保持を行っておらず、一般浴での入浴を行っていないが、入浴（特浴など）は行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１: 入浴は行っていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 整容 口腔ケア */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      口腔ケア
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-oral-care"
                        name="icf-oral-care"
                        value={oral_care}
                        onChange={(e) => setOralCare(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：義歯の手入れなどの口腔ケアを自分で行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：義歯の手入れなどの口腔ケアは自分では行っていないが、歯みがきは自分でセッティングして行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：自分でセッティングして歯を磨くことは行っていないが、セッティングをすれば、自分で歯みがきを行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：歯みがきのセッティングをしても自分では歯みがきを行っていないが、「うがい」は自分で行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：『うがい』を自分で行っていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 整容 整容 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      整容 整容
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-grooming"
                        name="icf-grooming"
                        value={dressing}
                        onChange={(e) => setDressing(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：爪を切ることを自分で行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：爪を切ることは自分で行っていないが、髭剃りやスキンケア、整髪は自分で行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：髭剃りやスキンケア、整髪は自分で行っていないが、洗顔は自分で行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：洗顔は自分で行っていないが、手洗いは自分で行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：手洗いを自分で行っていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 整容 衣服の着脱 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      整容 衣服の着脱
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-dressing"
                        name="icf-dressing"
                        value={dressing_action}
                        onChange={(e) => setDressingAction(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：衣服を畳んだり整理することは自分で行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：衣服を畳んだり整理することは自分で行っていないが、ズボンやパンツの着脱は自分で行っている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：ズボンやパンツの着脱は自分で行っていないが、更衣の際のボタンのかけはずしは自分で行っている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：更衣の際のボタンのかけはずしは自分で行っていないが、上衣の片袖を通すことは自分で行っている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：上衣の片袖を通すことを自分で行っていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 社会参加 余暇 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      社会参加 余暇
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-leisure"
                        name="icf-leisure"
                        value={leisure}
                        onChange={(e) => setLeisure(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：施設や家を１日以上離れる外出または旅行をしている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：旅行はしていないが、個人による趣味活動はしている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：屋外で行うような個人的趣味活動はしていないが、屋内でする程度のことはしている"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：集団レクリエーションへは参加していないが、一人でテレビを楽しんでいる"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：テレビを見たり、ラジオを聴いていない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* 社会参加 社会交流 */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom style={{ fontSize: '14px' }}>
                      社会参加 社会交流
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="icf-social-interaction"
                        name="icf-social-interaction"
                        value={communication}
                        onChange={(e) => setCommunication(e.target.value)}
                      >
                        <FormControlLabel
                          value="5"
                          control={<Radio color="primary" />}
                          label="５：情報伝達手段を用いて交流を行っている"
                        />
                        <FormControlLabel
                          value="4"
                          control={<Radio color="primary" />}
                          label="４：通信機器を用いて自ら連絡を取ることは行っていないが、援助があっての外出はしている"
                        />
                        <FormControlLabel
                          value="3"
                          control={<Radio color="primary" />}
                          label="３：外出はしていないが、親族・友人の訪問を受け会話している"
                        />
                        <FormControlLabel
                          value="2"
                          control={<Radio color="primary" />}
                          label="２：近所づきあいはしていないが、施設利用者や家族と会話はしている"
                        />
                        <FormControlLabel
                          value="1"
                          control={<Radio color="primary" />}
                          label="１：会話がない、していない、できない"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          <Grid item container xs={12}>
            <Grid item xs={12} sm={2}>
              <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                <GeneralPrimaryGhostButton
                  onClick={onCancel}
                  innerText={'保存せず戻る'}
                  setCss={'width: 120px; height: 40px;'}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box width={{ xs: '100%', sm: '200px' }}>
                <GeneralPrimaryButton txt="保存する" setCss={'width: 100px; height: 40px;'} onClick={handleSave} />
              </Box>
            </Grid>
            {/* 編集モードの場合のみ表示する */}
            {isEditMode && onDuplicate && (
              <Grid item xs={12} sm={2}>
                <Box width={{ xs: '100%', sm: '200px' }}>
                  <GeneralPrimaryGhostButton
                    onClick={() => onDuplicate(handleGetFormData())}
                    innerText={'複製する'}
                    setCss={'width: 100px; height: 40px;'}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={3}></Grid>
            {/* 編集モードの場合のみ表示する */}
            {isEditMode && onDelete && (
              <Grid item xs={12} sm={2}>
                <Box width={{ xs: '100%', sm: '200px' }}>
                  <GeneralPrimaryGhostButton
                    onClick={onDelete}
                    innerText={'削除する'}
                    setCss={'width: 100px; height: 40px;'}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ScientificNursingCarePromotionForm;
